// Listing modes for Customer Quotes — List / Process.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/ads-and-sbs/modes.ts, which is itself mirrored from
// src/blank/BlankLayout.tsx (Rule 0 — the canonical reference).
//
//   <base>/list      → QuoteList     (the working register)
//   <base>/process   → RFQProcess    (drop an RFQ, watch it become a quote)
//
// WHY THIS FILE EXISTS RATHER THAN LIVING IN THE LAYOUT. Blank keeps its
// vocabulary inside BlankLayout.tsx because it is mounted once. The Quotes
// component is mounted at FIVE nav keys — measured `grep -n -B1 "component:
// Quotes" src/app/Features.tsx` at 003205ac6: "quotes", "supplier_quotes",
// "supplier-quotes", "customer_quotes", "customer-quotes" (this line said
// THREE and omitted "quotes" and "supplier_quotes") — and the gates below have
// to be answerable from both the layout and the route table without two copies
// of the segment list drifting apart.
//
// ── PROCESS IS g2-ONLY, AND THAT IS DELIBERATE ───────────────────────────────
// One image serves every tenant (Dockerfile.cloudrun), so an ungated Process
// mode would put an RFQ processor — and a mode switcher the module has never
// had — onto the quotes register of all seven tenants. On anything but g2 this
// module keeps exactly one view, which by PATTERNS.md "Module weight" is the
// MINOR shape: no layout, no switcher, no redirect. Same gate style as
// src/dashboard/Dashboard.tsx:73.
import { getProduct } from "../config/product";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in Quotes.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  process: "RFQ Process",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Quote register",
  process: "Draft quotes from documents",
};

/**
 * List, not Process. The register IS the working surface — someone opens this
 * module to find a quote. Process is where you go to start a NEW one from a
 * customer's RFQ, which is a deliberate act, not a landing.
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * `<module>.viewMode`, underscored — the convention
 * src/__tests__/routeModeSegments.test.tsx pins for tech_log
 * and src/ads-and-sbs/modes.ts follows.
 */
export const MODE_STORAGE_KEY = "customer_quotes.viewMode";

/**
 * The remembered-mode key for THIS MOUNT.
 *
 * `MODE_STORAGE_KEY` is one string, and this component is mounted at five nav
 * keys. Lane Q1 kept the customer register's remembered Process mode safe by
 * refusing to WRITE from the supplier mount — sound while that mount had a
 * single mode and nothing to remember. Aaron's 2026-09-20 ruling gives it
 * Process back, so it has two modes and a genuine preference again, and that
 * guard would now silently drop it.
 *
 * Scoping the key is what preserves Q1's guarantee under the new design:
 * choosing List on Supplier Quotes must not reset Customer Quotes to List.
 *
 * The customer and ambiguous mounts keep the EXISTING string, so no user
 * loses a remembered mode to this change; only the supplier mounts move to a
 * key of their own.
 */
export function modeStorageKey(originalUrl?: string, title?: string): string {
  return mountQuoteTypeFilter(originalUrl, title) === "supplier"
    ? "supplier_quotes.viewMode"
    : MODE_STORAGE_KEY;
}

/**
 * Does this tenant get the mode switcher at all? See the g2-only note above.
 * Read through a function rather than a module-level const so tests can drive
 * the product resolver; `getProduct()` caches internally, so this is cheap.
 *
 * TENANT-WIDE ONLY. It answers "does this PRODUCT have Process", not "does
 * THIS MOUNT" — see `hasModeSwitcherForMount` below, which is what the route
 * table asks.
 */
export const hasModeSwitcher = (): boolean =>
  getProduct() === "g2" || getProduct() === "aquipe";

/**
 * Does THIS MOUNT get the Process mode?
 *
 * ── WHY A MOUNT GATE AND NOT JUST THE TENANT GATE ────────────────────────────
 * Process ingests a document travelling in ONE direction. The pipeline behind
 * it (src/sean/pipeline.ts -> POST /v6/autoquote/*) calls `resolve-customer`,
 * `create-customer` and `build-quote`, and the backend
 * (aeronet-fast-api/app/autoquote/api.py:596) writes `quote_type="customer"`
 * EXPLICITLY, its own comment calling this "a CUSTOMER's inbound RFQ getting a
 * quote back". So Process turns a customer's RFQ into a CUSTOMER quote — start
 * to finish, one direction. The drop zone's copy was never the bug.
 *
 * `Quotes.tsx` is mounted at five nav keys. `hasModeSwitcher()` above is
 * product-scoped, so on g2 the Process mode appeared on the SUPPLIER Quotes
 * register too, where its drop zone reads "Drop a customer RFQ" (Aaron,
 * 2026-09-19 and again 2026-09-20: *"I still 'drop a customer quote' in
 * supplier quotes"*). `mountQuoteTypeFilter` below already derives the mount's
 * direction for the register's default filter; the mode gate simply never
 * asked it.
 *
 * ⚠️ SUPERSEDED IN PART, 2026-09-20. The paragraph above records why the
 * supplier mount was EXCLUDED from Process. Aaron overruled that: the mount
 * keeps Process and creates a supplier quote instead. Everything above about
 * the pipeline's measured direction still holds and is why the direction now
 * has to be passed explicitly rather than assumed — see `processQuoteType`
 * below, which is what this gate now asks.
 *
 * Raising a supplier quote by hand remains a CREATE and still exists as "New
 * RFQ" in the register's CreateFab menu (QuoteToolBar.tsx:142-148 ->
 * `{ state: { type: QuoteType.Supplier } }`). That path is untouched; this one
 * is the document-drop route to the same kind of record.
 */
export const hasProcessMode = (originalUrl?: string, title?: string): boolean =>
  hasModeSwitcher() && processQuoteType(originalUrl, title) !== null;

/**
 * The direction a quote raised from THIS MOUNT's Process mode is created in —
 * `quote_type` on the row, and null when the mount gets no Process mode at all.
 *
 * ── AARON OVERRULED THE EXCLUSION, NOT THE DIAGNOSIS ─────────────────────────
 * The block above used to end `... !== "supplier"`, i.e. the supplier mount got
 * no Process mode. That followed from a correct measurement — the pipeline
 * writes a CUSTOMER quote end to end — but Aaron ruled on the direction rather
 * than the mount (2026-09-20): *"just should be supplier quote not customer
 * quote"*. Removing the mode answers a question he did not ask; what he wants
 * is for the supplier register's Process mode to produce a SUPPLIER quote.
 *
 * So the mount keeps its mode and chooses its DIRECTION. That direction is
 * carried to `POST /v6/autoquote/build-quote` as `quote_type`
 * (src/sean/pipeline.ts `BuildQuoteBody`), where it defaults to "customer" —
 * the value the endpoint hardcoded before, and still the right one for every
 * mount that does not say otherwise. The shared default is not flipped to
 * satisfy one mount.
 *
 * Derived from `mountQuoteTypeFilter` rather than a second hardcoded list, so
 * the register's default FILTER and the direction Process WRITES can never
 * disagree — a quote that did not appear in the register it was raised from is
 * exactly the defect Aaron reported.
 *
 * An ambiguous mount (the bare "quotes" nav key, which names neither
 * direction) resolves to "customer": that is what it has always created, and
 * inferring "supplier" from the absence of a word would be a guess.
 */
export function processQuoteType(
  originalUrl?: string,
  title?: string
): "customer" | "supplier" | null {
  if (!hasModeSwitcher()) return null;
  return mountQuoteTypeFilter(originalUrl, title) === "supplier" ? "supplier" : "customer";
}

/**
 * The mode vocabulary THIS MOUNT offers — `MODE_LABELS` minus anything the
 * mount does not get. Built by DELETION from the canonical object rather than
 * reassembled from literals, so a mode added to `MODE_LABELS` is offered here
 * automatically instead of being silently dropped by a stale copy.
 */
export function modeLabelsForMount(
  originalUrl?: string,
  title?: string
): { [segment: string]: string } {
  if (hasProcessMode(originalUrl, title)) return MODE_LABELS;
  const { process: _processIsCustomerSide, ...rest } = MODE_LABELS;
  return rest;
}

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: this module has never had a view-mode switcher of
 * any kind, so nothing has ever written this key. There is no old shape to
 * accept, and anything already stored under it is not ours — falling through to
 * the default is the right handling. (src/opportunities/modes.ts carries a
 * migration because its key genuinely held a previous shape; this one does
 * not.)
 */
export function readRememberedModeSegment(
  originalUrl?: string,
  title?: string
): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(modeStorageKey(originalUrl, title));
  // Read from the MOUNT's own key (see `modeStorageKey`) and still validated
  // against the MOUNT's vocabulary. Both halves are load-bearing: the key stops
  // one register's preference leaking into another's, and the validation stops
  // a stored segment the mount does not offer from sending the index route to
  // a mode it would only forward away from.
  const offered = modeLabelsForMount(originalUrl, title);
  return stored && Object.prototype.hasOwnProperty.call(offered, stored)
    ? stored
    : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment.
 *
 * The base is derived from the LIVE pathname rather than hardcoded because this
 * module is mounted at three nav keys — a hardcoded base would pass at the
 * canonical path and break at the aliases, which is exactly what
 * routeModeSegments.test.tsx mounts non-canonically to catch.
 */
/**
 * Default `quote_type` filter for the register, derived from the mount
 * identity — the same nav-key/title signal Features.tsx fans out to reach
 * this one component from three different registers (see file header).
 *
 * Without this, every mount's default filter was the same hardcoded "Any"
 * (QuoteDataGrid.advancedSearchDefaultItems), so customer_quotes and
 * supplier-quotes both listed every quote regardless of its real
 * `quote_type` — the "Type" column was the only thing distinguishing them,
 * and a mistyped row (see build_quote's default) was invisible as a bug.
 *
 * Checked against BOTH the nav url and the nav label because either can carry
 * the underscore/hyphen variant depending on where the mount came from (the
 * DEFAULT_G2_NAV fallback literal in App.tsx vs a live /v6/nav row) — App.tsx
 * currently only guarantees `title`, so this can't rely on `originalUrl`
 * alone.
 */
export function mountQuoteTypeFilter(
  originalUrl?: string,
  title?: string
): "" | "customer" | "supplier" {
  const haystack = `${originalUrl ?? ""} ${title ?? ""}`.toLowerCase();
  if (haystack.includes("supplier")) return "supplier";
  if (haystack.includes("customer")) return "customer";
  return "";
}

export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
